import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { cartApi, wishlistApi, recentlyViewedApi } from "./api";
import { toast } from "sonner";
import { t } from "./i18n";

const StoreContext = createContext(null);

// Simple persistent anonymous customer id. Replace with Customer Account API user id after login.
function ensureCustomerId() {
  let cid = localStorage.getItem("marcini:cid");
  if (!cid) {
    cid = "anon-" + crypto.randomUUID();
    localStorage.setItem("marcini:cid", cid);
  }
  return cid;
}

// Marcini is currently NO-only. Kept in context to preserve API shape for later markets.
const MARKET = "NO";

export function StoreProvider({ children }) {
  const [cart, setCart] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const customerId = useMemo(() => ensureCustomerId(), []);

  useEffect(() => {
    (async () => {
      const key = `marcini:cart:${MARKET}`;
      const existing = localStorage.getItem(key);
      try {
        if (existing) {
          const c = await cartApi.get(existing, MARKET);
          setCart(c);
          return;
        }
      } catch (_) {
        localStorage.removeItem(key);
      }
      const created = await cartApi.create(MARKET);
      localStorage.setItem(key, created.id);
      setCart(created);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [wl, rv] = await Promise.all([
          wishlistApi.list(customerId),
          recentlyViewedApi.list(customerId),
        ]);
        setWishlist(wl.map(x => x.product_handle));
        setRecentlyViewed(rv.map(x => x.product_handle));
      } catch (_) { /* ignore */ }
    })();
  }, [customerId]);

  const addToCart = useCallback(async (variantId, qty = 1) => {
    try {
      const updated = await cartApi.add({
        cart_id: cart?.id,
        variant_id: variantId,
        quantity: qty,
        market: MARKET,
      });
      if (updated.id !== cart?.id) {
        localStorage.setItem(`marcini:cart:${MARKET}`, updated.id);
      }
      setCart(updated);
      setCartOpen(true);
      // Drawer opens = visual confirmation. Suppress "Lagt til"-toast so it
      // never overlaps the "Gå til kassen" button on mobile.
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.toasts.addFail);
    }
  }, [cart]);

  const addMultipleToCart = useCallback(async (lines) => {
    try {
      const updated = await cartApi.addMultiple({
        cart_id: cart?.id,
        lines,
        market: MARKET,
      });
      if (updated.id !== cart?.id) {
        localStorage.setItem(`marcini:cart:${MARKET}`, updated.id);
      }
      setCart(updated);
      setCartOpen(true);
      // Bundle-added: same reasoning — drawer opening is the confirmation.
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.toasts.addFail);
    }
  }, [cart]);

  const updateLine = useCallback(async (lineId, quantity) => {
    if (!cart) return;
    const updated = await cartApi.update({ cart_id: cart.id, line_id: lineId, quantity });
    setCart(updated);
  }, [cart]);

  const removeLine = useCallback(async (lineId) => {
    if (!cart) return;
    const updated = await cartApi.remove({ cart_id: cart.id, line_id: lineId });
    setCart(updated);
  }, [cart]);

  const toggleWishlist = useCallback(async (handle) => {
    if (wishlist.includes(handle)) {
      await wishlistApi.remove(customerId, handle);
      setWishlist(w => w.filter(h => h !== handle));
      toast(t.toasts.removedWishlist);
    } else {
      await wishlistApi.add(customerId, handle);
      setWishlist(w => [...w, handle]);
      toast.success(t.toasts.savedWishlist);
    }
  }, [wishlist, customerId]);

  const trackView = useCallback(async (handle) => {
    try {
      await recentlyViewedApi.add(customerId, handle);
      setRecentlyViewed(rv => [handle, ...rv.filter(h => h !== handle)].slice(0, 20));
    } catch (_) {}
  }, [customerId]);

  const value = {
    market: MARKET,
    cart, cartOpen, setCartOpen,
    addToCart, addMultipleToCart, updateLine, removeLine,
    wishlist, toggleWishlist,
    recentlyViewed, trackView,
    customerId,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used inside StoreProvider");
  return ctx;
}

export function formatPrice(price) {
  if (!price) return "";
  const amt = parseFloat(price.amount);
  const cur = price.currencyCode;
  if (cur === "NOK") return `${Math.round(amt).toLocaleString("no-NO")} kr`;
  if (cur === "EUR") return `€${amt.toFixed(2)}`;
  if (cur === "USD") return `$${amt.toFixed(2)}`;
  return `${amt} ${cur}`;
}
